import { WORMHOLE_OUTPUT_LENGTHS } from "./constants.js";

/** Approximate word target for a Write length slider index (matches server Write prose resolution). */
export function approxWordsFromWormholeOutputLength(outputLength?: number | null): number | undefined {
  if (typeof outputLength !== "number" || !Number.isFinite(outputLength)) return undefined;
  const matched = WORMHOLE_OUTPUT_LENGTHS.find((entry) => entry.value === outputLength);
  if (!matched) return undefined;
  const fromLabel = Number(String(matched.label).replace(/[^\d]/g, ""));
  if (Number.isFinite(fromLabel) && fromLabel > 0) return fromLabel;
  if (typeof matched.maxTokens === "number" && matched.maxTokens > 0) {
    return Math.round(matched.maxTokens / 1.2);
  }
  return undefined;
}

/**
 * Write length for agentic chat: the live editor slider is authoritative for this session.
 * Persisted userData is only used when the editor has not set a length yet.
 */
export function resolveWormholeOutputLengthForAgenticChat(
  editorValue?: number | null,
  persistedValue?: number | null,
): number | undefined {
  if (typeof editorValue === "number" && Number.isFinite(editorValue)) {
    return editorValue;
  }
  if (typeof persistedValue === "number" && Number.isFinite(persistedValue)) {
    return persistedValue;
  }
  return undefined;
}

/**
 * Prefer the slider index with the larger approximate word target when editor state and
 * persisted userData disagree (common when editorOptions lags account defaults).
 * Use for one-time Write Settings hydration — not for chat payloads (see resolveWormholeOutputLengthForAgenticChat).
 */
export function pickWormholeOutputLengthIndex(
  editorValue?: number | null,
  persistedValue?: number | null,
): number | undefined {
  const candidates = [editorValue, persistedValue].filter(
    (v): v is number => typeof v === "number" && Number.isFinite(v),
  );
  if (!candidates.length) return undefined;
  let best = candidates[0];
  let bestWords = approxWordsFromWormholeOutputLength(best) ?? 0;
  for (const candidate of candidates.slice(1)) {
    const words = approxWordsFromWormholeOutputLength(candidate) ?? 0;
    if (words > bestWords) {
      best = candidate;
      bestWords = words;
    }
  }
  return best;
}

/** True when Write length target should expand far beyond a short selection excerpt. */
export function writeLengthSuggestsSelectionExpansion(writeTargetWords: number, passageWordCount: number): boolean {
  return passageWordCount >= 40 && writeTargetWords >= passageWordCount * 1.75 + 80;
}

/** Acceptable word-count band (~±12%) for steering prose length toward a target. */
export function formatWriteLengthAcceptableBand(
  targetWords: number,
  maxWords = 6000,
  options?: { minBandFloor?: number },
): { low: number; high: number; target: number } {
  const minBandFloor = options?.minBandFloor ?? 50;
  const target = Math.max(minBandFloor, Math.floor(targetWords));
  const low = Math.max(minBandFloor, Math.floor(target * 0.88));
  const high = Math.min(maxWords, Math.ceil(target * 1.12));
  return { low, high, target };
}
